import {
  ApplicationConfig,
  importProvidersFrom,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Title } from '@angular/platform-browser';

import { NgxsModule } from '@ngxs/store';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';

import { provideToastr } from 'ngx-toastr';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { ModalModule } from 'ngx-bootstrap/modal';

import { provideBaseUrl, providePageTitleStrategy, provideStateLogoutPlugin } from '@core/provider';
import { baseHttpInterceptor } from '@core/interceptor';
import {
  AccountState,
  AuthState,
  FavoriteState,
  HomeState,
  ProductState,
  QuoteDetailState,
  QuoteListState,
  RegisterState,
} from '@data/state';
import { AppLanguage } from '@data/enum';
import { isDateExpired } from '@shared/util';
import { GenericToastComponent } from '@shared/component';

import { environment } from '@env';
import { routes } from './app.routes';

export const appConfig: ApplicationConfig = {
  providers: [
    Title,
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'top', anchorScrolling: 'enabled' }),
    ),
    provideHttpClient(withInterceptors([baseHttpInterceptor])),
    provideAnimations(),
    provideToastr({
      toastComponent: GenericToastComponent,
      positionClass: 'toast-bottom-right',
      progressBar: true,
      closeButton: true,
      timeOut: 4000,
      extendedTimeOut: 2000,
    }),
    provideTranslateService({
      lang: AppLanguage.EN,
      fallbackLang: AppLanguage.EN,
      loader: provideTranslateHttpLoader({ prefix: '/i18n/', suffix: '.json' }),
    }),
    provideBaseUrl(),
    providePageTitleStrategy(),
    provideStateLogoutPlugin(),
    importProvidersFrom(
      NgxsModule.forRoot(
        [
          AccountState,
          AuthState,
          FavoriteState,
          HomeState,
          ProductState,
          QuoteDetailState,
          QuoteListState,
          RegisterState,
        ],
        {
          developmentMode: !environment.production,
          selectorOptions: {
            suppressErrors: false,
            injectContainerState: false,
          },
        },
      ),
      NgxsStoragePluginModule.forRoot({
        keys: [AuthState],
        afterDeserialize: (obj, key) => {
          if (key === 'auth' && obj?.token && isDateExpired(obj.refreshTokenExpiration)) {
            return null;
          }
          return obj;
        },
      }),
      NgxsLoggerPluginModule.forRoot({ disabled: environment.production }),
      NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.production }),
      ModalModule,
    ),
  ],
};
